<template>
  <div ref="containerRef" class="breadcrumbs">
    <template v-for="(node, idx) in visibleItems" :key="node.key">
      <div
        v-if="node.kind === 'item'"
        class="breadcrumbs__option"
        :class="{ 'is-last': idx === visibleItems.length - 1 }"
        @click="!isLastItem(idx) && onClicked(node.option)"
      >
        <ButtonText
          v-if="node.option.icon"
          :class="['breadcrumbs__icon-item', isSingle ? 'no-hover' : '']"
          buttonText=""
          size="extra-small"
          @onClicked="!isLastItem(idx) && onClicked(node.option)"
        >
          <template #left>
            <component :is="node.option.icon" />
          </template>
        </ButtonText>
        <TextBase
          v-else
          :text="node.option.title"
          class="breadcrumbs__text"
          type="label"
          :style="{ maxWidth: getTextItemCalculatedMaxWidth(node.key) }"
          overflow="ellipsis"
          text-wrap="nowrap"
          overflow-tooltip
        />
      </div>

      <div v-else ref="ellipsisWrapperRef" class="breadcrumbs__option breadcrumbs__ellipsis-item">
        <div style="position: relative">
          <p class="dot" @click="toggleEllipsisDropdown">…</p>

          <div v-if="showEllipsisDropdown" class="ellipsis-dropdown">
            <ul>
              <li
                v-for="item in node.hidden"
                :key="item.id ?? item.title"
                @click="selectHiddenOption(item)"
                @mouseenter="hoveredItemId = item.id ?? item.title"
                @mouseleave="hoveredItemId = null"
              >
                <TextBase
                  :text="item.title"
                  class="breadcrumbs__text"
                  type="label"
                  overflow="ellipsis"
                  text-wrap="nowrap"
                  overflow-tooltip
                  :color="hoveredItemId === (item.id ?? item.title) ? 'white' : 'black'"
                />
              </li>
            </ul>
          </div>
        </div>
      </div>

      <span v-if="idx < visibleItems.length - 1" class="breadcrumbs__separator">
        <slot />
      </span>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onBeforeUnmount, nextTick, computed, toRefs, watch } from 'vue'
import type { BreadcrumbOption } from '@/utils/breadcrumbs'
import ButtonText from './buttons/ButtonText.vue'
import TextBase from './TextBase.vue'

interface ItemNode {
  kind: 'item'
  option: BreadcrumbOption
  key: string | number
}
interface EllipsisNode {
  kind: 'ellipsis'
  hidden: BreadcrumbOption[]
  key: 'ellipsis'
}
type CrumbNode = ItemNode | EllipsisNode

const props = defineProps<{ breadcrumbOptions: BreadcrumbOption[] }>()
const { breadcrumbOptions } = toRefs(props)
const emits = defineEmits<(e: 'onClicked', option: BreadcrumbOption) => void>()
const isSingle = computed(() => breadcrumbOptions.value.length === 1)
const containerRef = ref<HTMLElement | null>(null)
const hiddenCount = ref(0)
const showEllipsisDropdown = ref(false)
const ellipsisWrapperRef = ref<HTMLElement[]>([])
const itemTextRefs = ref<Map<string | number, HTMLElement | null>>(new Map())
const calculatedTextMaxWidths = ref<Map<string | number, string>>(new Map())
const hoveredItemId = ref<string | number | null>(null)

let previousContainerWidth = 0
let ro: ResizeObserver | undefined

watch(
  breadcrumbOptions,
  () => {
    hiddenCount.value = 0
    previousContainerWidth = 0
    itemTextRefs.value.clear()
    calculatedTextMaxWidths.value.clear()
    nextTick(measure)
  },
  { deep: true }
)

onMounted(() => {
  if (!containerRef.value) return
  ro = new ResizeObserver((entries) => {
    const entry = entries[0]
    if (entry && entry.contentRect.width !== previousContainerWidth) {
      // Only run measure if width actually changed.
      // We reset previousContainerWidth inside measure AFTER deciding if hiddenCount needs to change
      // or if it's time to recalculate text widths.
      nextTick(measure)
    }
  })
  ro.observe(containerRef.value)
  nextTick(measure)
})

onBeforeUnmount(() => {
  if (ro) {
    ro.disconnect()
  }
})
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
const ensureKey = (option: BreadcrumbOption, index: number, prefix: string = '') =>
  option.id ?? `${prefix}${index}`

const visibleItems = computed<CrumbNode[]>(() => {
  const opts = breadcrumbOptions.value
  const total = opts.length
  const hc = hiddenCount.value

  if (total === 0) return []

  if (total <= 3) {
    return opts.map((o, i) => ({ kind: 'item', option: o, key: ensureKey(o, i, 'all') }))
  }
  if (hc === 0) {
    return opts.map((o, i) => ({ kind: 'item', option: o, key: ensureKey(o, i, 'all_hc0') }))
  }

  const maxHideActual = total - 3
  if (hc >= maxHideActual && total > 3) {
    const hiddenAll = opts.slice(2, total - 1)
    return [
      { kind: 'item', option: opts[0], key: ensureKey(opts[0], 0, 'fh_') },
      { kind: 'item', option: opts[1], key: ensureKey(opts[1], 1, 'fh_') },
      { kind: 'ellipsis', hidden: hiddenAll, key: 'ellipsis' },
      { kind: 'item', option: opts[total - 1], key: ensureKey(opts[total - 1], total - 1, 'fh_') }
    ]
  }

  const hidden = opts.slice(2, 2 + hc)
  const tail = opts.slice(2 + hc)
  const result: CrumbNode[] = [
    { kind: 'item', option: opts[0], key: ensureKey(opts[0], 0, 'ph_') },
    { kind: 'item', option: opts[1], key: ensureKey(opts[1], 1, 'ph_') },
    { kind: 'ellipsis', hidden, key: 'ellipsis' }
  ]
  tail.forEach((o, i) =>
    result.push({ kind: 'item', option: o, key: ensureKey(o, i + 2 + hc, 'ph_tail_') })
  )
  return result
})

function onClicked(option: BreadcrumbOption) {
  emits('onClicked', option)
}

function isLastItem(index: number): boolean {
  return index === visibleItems.value.length - 1
}

function measure() {
  const el = containerRef.value
  if (!el || breadcrumbOptions.value.length === 0) {
    calculatedTextMaxWidths.value.clear()
    previousContainerWidth = el ? el.clientWidth : 0
    return
  }

  const containerClientWidth = el.clientWidth
  const currentHiddenCount = hiddenCount.value

  // Temporarily remove maxWidth constraints to measure natural scrollWidth
  const oldMaxWidths = new Map(calculatedTextMaxWidths.value)
  calculatedTextMaxWidths.value.clear()

  nextTick(() => {
    if (!containerRef.value) {
      // Component might have unmounted
      calculatedTextMaxWidths.value = oldMaxWidths // Restore if unmounted during tick
      return
    }

    let newHiddenCount = currentHiddenCount
    const scrollWidth = el.scrollWidth
    const totalOptions = breadcrumbOptions.value.length
    const maxPossibleToHide = Math.max(0, totalOptions - 3)

    if (containerClientWidth < previousContainerWidth && currentHiddenCount === 0) {
      // If width decreased and nothing is hidden, we might need to hide something
      if (scrollWidth > containerClientWidth && newHiddenCount < maxPossibleToHide) {
        newHiddenCount++
      }
    } else if (containerClientWidth > previousContainerWidth && currentHiddenCount > 0) {
      // If container grew and items are hidden, try to show more by resetting hiddenCount
      // This will trigger a re-evaluation of hiddenCount based on new scrollWidth
      newHiddenCount = 0
    } else if (scrollWidth > containerClientWidth) {
      // Standard overflow case: try to hide more if possible
      if (newHiddenCount < maxPossibleToHide) {
        newHiddenCount++
      }
    }
    // If scrollWidth <= containerClientWidth and newHiddenCount === 0, we are good.
    // If scrollWidth <= containerClientWidth and newHiddenCount > 0,
    // it means we might have space to unhide. The "container grew" condition handles this.

    previousContainerWidth = containerClientWidth // Update after comparisons

    if (newHiddenCount !== currentHiddenCount) {
      hiddenCount.value = newHiddenCount
      // `hiddenCount` change will trigger re-render and `visibleItems` update.
      // Then `measure` will be called again by the watcher on `visibleItems` (implicitly via DOM changes) or ResizeObserver.
      // We need to ensure `measure` is called again after DOM update from new `hiddenCount`.
      nextTick(measure) // Re-measure after hiddenCount change applies
      return
    }

    // If hiddenCount is stable, restore oldMaxWidths before recalculating
    calculatedTextMaxWidths.value = oldMaxWidths
    // Then, update text item properties (maxWidths and overflow flags)
    nextTick(() => {
      if (!containerRef.value) return
      updateTextItemProperties()
    })
  })
}

function updateTextItemProperties() {
  const el = containerRef.value
  if (!el || visibleItems.value.length === 0) {
    calculatedTextMaxWidths.value.clear()
    return
  }

  const containerTotalWidth = el.clientWidth
  let occupiedByNonText = 0
  const textItemKeysForDynamicWidth: (string | number)[] = []

  // Use current DOM elements for accurate width measurements
  const childrenElements = Array.from(el.children) as HTMLElement[]
  let childElIdx = 0

  visibleItems.value.forEach((node, vIdx) => {
    const itemWrapperElement = childrenElements[childElIdx++]
    if (!itemWrapperElement) return

    if (node.kind === 'item') {
      if (node.option.icon) {
        occupiedByNonText += itemWrapperElement.offsetWidth
      } else {
        textItemKeysForDynamicWidth.push(node.key)
      }
    } else {
      // Ellipsis node
      occupiedByNonText += itemWrapperElement.offsetWidth
    }

    if (vIdx < visibleItems.value.length - 1) {
      const separatorElement = childrenElements[childElIdx++]
      if (separatorElement) {
        occupiedByNonText += separatorElement.offsetWidth
      }
    }
  })

  const newCalculatedMaxWidths = new Map<string | number, string>()
  if (textItemKeysForDynamicWidth.length > 0) {
    const availableWidthForTextItems = Math.max(0, containerTotalWidth - occupiedByNonText)
    // Ensure a minimum sensible width for readability, especially if multiple text items.
    // e.g. if only one text item, it can take more space.
    const minSensibleWidthPerItem = textItemKeysForDynamicWidth.length === 1 ? 80 : 40
    const dynamicWidthPerTextItem = Math.max(
      minSensibleWidthPerItem,
      availableWidthForTextItems / textItemKeysForDynamicWidth.length
    )

    textItemKeysForDynamicWidth.forEach((key) => {
      newCalculatedMaxWidths.set(key, `${dynamicWidthPerTextItem}px`)
    })
  }
  calculatedTextMaxWidths.value = newCalculatedMaxWidths
}

function getTextItemCalculatedMaxWidth(itemKey: string | number): string | undefined {
  return calculatedTextMaxWidths.value.get(itemKey)
}
function toggleEllipsisDropdown() {
  showEllipsisDropdown.value = !showEllipsisDropdown.value
}
function selectHiddenOption(option: BreadcrumbOption) {
  emits('onClicked', option)
  showEllipsisDropdown.value = false
}
function handleClickOutside(event: MouseEvent) {
  const refs = ellipsisWrapperRef.value as HTMLElement[]

  const clickedInsideAny = refs.some((el) => {
    return el instanceof HTMLElement && el.contains(event.target as Node)
  })

  if (!clickedInsideAny && showEllipsisDropdown.value) {
    showEllipsisDropdown.value = false
  }
}
</script>

<style scoped lang="scss">
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/font-variables.scss';
@use '@/design-system/design-tokens/variables.scss';

.breadcrumbs {
  display: flex;
  align-items: center;
  overflow: relative;
  width: 100%;
  height: 16px;

  &__option {
    display: flex;
    align-items: center;
    cursor: pointer;
    flex-shrink: 0;

    &.is-last {
      cursor: default;
      color: black;

      .breadcrumbs__text {
        text-decoration: none;
        color: variables.$color-greyscale-content;
      }
      .breadcrumbs__icon-item {
        cursor: default !important;
      }
    }

    &:not(.is-last) {
      .breadcrumbs__icon-item {
        &:hover,
        &:focus-within {
          color: white;
        }
      }
    }
  }

  &__icon-item {
    flex-shrink: 0;
    display: inline-flex;
    color: black;
  }

  &__text {
    color: variables.$color-greyscale-content;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-decoration: underline;
    text-underline-offset: 2px;
    @include font-mixins.desktop-regular-label;
  }

  &__ellipsis-item {
    flex-shrink: 0;
  }

  &__separator {
    display: inline-flex;
    align-items: center;
    margin: 0 variables.$margin-medium;
    flex-shrink: 0;
  }
}

.dot {
  cursor: pointer;
  @include font-mixins.desktop-regular-label;
  text-decoration: underline;
  text-underline-offset: 2px;
  padding: 0 4px;
}

.ellipsis-dropdown {
  position: absolute;
  top: 25%;
  left: 100%;
  background: white;
  border: 1px solid #767676;
  border-radius: 12px;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16);
  padding: 4px 0;
  z-index: 1000;
  max-width: 246px;
  min-width: 180px;
  white-space: nowrap;
  overflow: hidden;

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    padding: 8px 12px;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      background-color: #2c4eff;
      color: white;
    }
  }
}
</style>
